/* ============================================================================
   Bro Glo — Upsell Drawer JavaScript (v2)
   ============================================================================
   Reads settings from window.brogloUpsellSettings (set by the upsell section)
   and product data from window.brogloPdpData (set by the PDP section).
   ============================================================================ */

(function () {
  'use strict';

  const BrogloUpsell = {

    isOpen: false,
    sourceSectionId: null,
    missingItems: [],
    alreadyAddedItems: [],
    selectedUpsells: {},
    timerInterval: null,

    /* ======================== PDP RESOLVER ======================== */
    /**
     * Resolves state and data from whichever PDP type loaded this page.
     * Supports: BrogloPdp (face tanner) and BrogloPdpTiered (body tanner).
     */
    _resolvePdp(sectionId) {
      let pdpState = null;
      let pdpData = null;
      let pdpType = null;

      // Try tiered PDP first (newer)
      if (window.BrogloPdpTiered?.state?.[sectionId]) {
        pdpState = window.BrogloPdpTiered.state[sectionId];
        pdpData = window.brogloPdpTieredData?.[sectionId];
        pdpType = 'tiered';
      }
      // Fall back to standard PDP
      else if (window.BrogloPdp?.state?.[sectionId]) {
        pdpState = window.BrogloPdp.state[sectionId];
        pdpData = window.brogloPdpData?.[sectionId];
        pdpType = 'standard';
      }
      // Last resort: check data objects directly
      else if (window.brogloPdpTieredData?.[sectionId]) {
        pdpData = window.brogloPdpTieredData[sectionId];
        pdpType = 'tiered';
      }
      else if (window.brogloPdpData?.[sectionId]) {
        pdpData = window.brogloPdpData[sectionId];
        pdpType = 'standard';
      }

      return { pdpState, pdpData, pdpType };
    },

    /** Returns true if currently configured as modal (vs drawer) */
    _isModal() {
      const overlay = document.getElementById('brogloUpsellOverlay');
      return overlay ? overlay.classList.contains('broglo-modal-mode') : false;
    },

    /** Hide main page content from screen readers when modal is open (WCAG 2.2 AA) */
    _setPageAriaHidden(hidden) {
      // Only apply aria-hidden for modal mode - drawer is less disruptive
      if (!this._isModal()) return;
      const targets = document.querySelectorAll(
        'body > *:not(#brogloUpsellOverlay):not(script):not(style)'
      );
      targets.forEach(el => {
        if (hidden) {
          if (!el.hasAttribute('aria-hidden')) {
            el.setAttribute('aria-hidden', 'true');
            el.dataset.brogloAriaHiddenAdded = 'true';
          }
        } else {
          if (el.dataset.brogloAriaHiddenAdded === 'true') {
            el.removeAttribute('aria-hidden');
            delete el.dataset.brogloAriaHiddenAdded;
          }
        }
      });
    },

    /* ======================== OPEN ======================== */
    open(sectionId, missingItems, alreadyAdded) {
      console.log('[Bro Glo Upsell] Opening - missingItems:', missingItems);
      this.sourceSectionId = sectionId;
      this.missingItems = missingItems;
      this.alreadyAddedItems = alreadyAdded;
      this.selectedUpsells = {};

      const overlay = document.getElementById('brogloUpsellOverlay');
      if (!overlay) return;

      // ── Reset CTA button from any previous open/add state ──
      const ctaBtnReset = document.getElementById('brogloUpsellCtaBtn');
      if (ctaBtnReset) {
        ctaBtnReset.style.removeProperty('background');
        ctaBtnReset.removeAttribute('aria-busy');
        ctaBtnReset.removeAttribute('aria-disabled');
      }

      const { pdpState, pdpData, pdpType } = this._resolvePdp(sectionId);
      this._pdpType = pdpType;
      console.log('[Bro Glo Upsell] Resolved PDP type:', pdpType);

      const settings = window.brogloUpsellSettings || {};

      this.updateConfirmation(pdpState, pdpData, alreadyAdded);
      this.renderCards(missingItems, pdpState, pdpData);
      this.updatePitch(missingItems, pdpData);

      // Pre-select visible items if enabled
      const preselect = settings.preselect !== false;
      document.querySelectorAll('.broglo-upsell__product').forEach(p => {
        const isHidden = p.style.getPropertyValue('display') === 'none';
        if (preselect && !isHidden) {
          p.classList.add('selected');
          p.setAttribute('aria-checked', 'true');
          this.selectedUpsells[p.dataset.upsellItem] = {
            variantId: parseInt(p.dataset.variantId),
            price: parseInt(p.dataset.price)
          };
        } else {
          p.classList.remove('selected');
          p.setAttribute('aria-checked', 'false');
        }
      });

      this.updateCardPrices(); // Recalc with pre-selected items
      this.updateConfirmationPrice();
      this.updateTierBar();
      this.updateCta();
      this.startTimer(settings);

      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      this._setPageAriaHidden(true);

      // Focus trap — find all focusable elements in drawer
      this._setupFocusTrap();

      setTimeout(() => {
        overlay.querySelector('.broglo-upsell__close')?.focus();
      }, 100);
    },

    /* ======================== CLOSE ======================== */
    close(skipCartOpen) {
      const overlay = document.getElementById('brogloUpsellOverlay');
      if (!overlay) return;
      overlay.classList.remove('open');
      document.body.style.overflow = '';
      this.isOpen = false;
      this._setPageAriaHidden(false);
      this._removeFocusTrap();

      // Always refresh cart after closing — items were added before upsell opened
      if (!skipCartOpen) {
        setTimeout(() => { this._openCart(); }, 300);
      }
    },

    closeOnBackdrop(e) {
      if (e.target === document.getElementById('brogloUpsellOverlay')) this.close();
    },

    skip() {
      this.close();
    },

    /** Unified cart opener — checks all possible drawer methods */
    _openCart() {
      if (window.BWCart?.refresh) { BWCart.refresh(true); return; }
      if (window.BrogloPdpTiered?.openCartDrawer) { BrogloPdpTiered.openCartDrawer(); return; }
      if (window.BrogloPdp?.openCartDrawer) { BrogloPdp.openCartDrawer(); return; }
      if (typeof CartDrawer !== 'undefined' && CartDrawer.open) { CartDrawer.open(); return; }
      window.location.href = '/cart';
    },

    /* ======================== COUNTDOWN TIMER ======================== */
    startTimer(settings) {
      const timerEl = document.getElementById('brogloUpsellTimer');
      const countEl = document.getElementById('brogloUpsellTimerCount');
      if (!timerEl || !countEl) return;

      const durationMs = (settings.timerMinutes || 3) * 60 * 1000;

      // Session-locked: persists across open/close
      let endTime;
      const stored = sessionStorage.getItem('broglo_upsell_timer');
      if (stored) {
        endTime = parseInt(stored);
        if (endTime <= Date.now()) {
          endTime = Date.now() + durationMs;
          sessionStorage.setItem('broglo_upsell_timer', endTime.toString());
        }
      } else {
        endTime = Date.now() + durationMs;
        sessionStorage.setItem('broglo_upsell_timer', endTime.toString());
      }

      if (this.timerInterval) clearInterval(this.timerInterval);

      const tick = () => {
        const remaining = Math.max(0, endTime - Date.now());
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        countEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;

        if (remaining <= 0) {
          clearInterval(this.timerInterval);
          countEl.textContent = '0:00';

          /* ── Timer expired: update messaging only, discounts stay active ── */
          timerEl.classList.add('expired');

          /* Swap timer text to urgency message */
          timerEl.innerHTML = '<span class="broglo-upsell__timer-expired">⚠️ Time\'s up — add these now to keep your bundle pricing</span>';

          /* Update pitch to reinforce urgency */
          const pitchTitle = document.getElementById('brogloUpsellPitchTitle');
          const pitchHighlight = document.getElementById('brogloUpsellPitchHighlight');
          if (pitchTitle) pitchTitle.textContent = 'Last chance to save';
          if (pitchHighlight) pitchHighlight.textContent = 'add now before your bundle pricing expires';
        } else if (remaining <= 60000) {
          timerEl.classList.add('expired');
        } else {
          timerEl.classList.remove('expired');
        }
      };

      tick();
      this.timerInterval = setInterval(tick, 1000);
    },

    /* ======================== RENDER CARDS (Dynamic) ======================== */
    renderCards(missingItems, pdpState, pdpData) {
      const container = document.getElementById('brogloUpsellProducts');
      if (!container) return;
      container.innerHTML = '';

      const upsellConfig = pdpData?.upsellConfig || {};
      const bundleItems = pdpData?.bundleItems || {};
      const isTiered = this._pdpType === 'tiered';

      console.log('[Bro Glo Upsell] renderCards - missingItems:', missingItems, 'type:', this._pdpType);

      missingItems.forEach(itemKey => {
        let productData = null;
        let desc = '';
        let tagText = '';
        let showTag = false;
        let socialText = '';

        if (isTiered) {
          // ── Tiered PDP: product data is directly in upsellConfig ──
          const product = upsellConfig[itemKey];
          if (product) {
            const shade = pdpState?.currentShade || 'original';

            // Check for shade B product (separate product for darker shade)
            if (product.shadeB && shade !== 'original') {
              productData = {
                ...product,
                variantId: product.shadeB.variantId,
                price: product.shadeB.price,
                title: product.shadeB.title,
                image: product.shadeB.image
              };
            }
            // Check for preferred variant (e.g. 'mitt' on an applicator product)
            else if (product.preferredVariant && product.variants && product.variants.length > 1) {
              const preferred = product.variants.find(v => v.handle === product.preferredVariant && v.available)
                || product.variants.find(v => v.available)
                || product.variants[0];
              if (preferred) {
                productData = {
                  ...product,
                  variantId: preferred.variantId,
                  price: preferred.price,
                  title: preferred.title,
                  image: preferred.image || product.image
                };
              } else {
                productData = product;
              }
            }
            // Otherwise try variant matching within the single product
            else if (product.variants && product.variants.length > 1) {
              const matched = product.variants.find(v => v.handle === shade && v.available)
                || product.variants.find(v => v.available)
                || product.variants[0];
              if (matched) {
                productData = {
                  ...product,
                  variantId: matched.variantId,
                  price: matched.price,
                  title: `${product.title} — ${matched.title}`,
                  image: matched.image || product.image
                };
              } else {
                productData = product;
              }
            } else {
              productData = product;
            }
            desc = product.desc || '';
            socialText = product.social || '';
          }
        } else {
          // ── Standard PDP: data split between bundleItems + upsellConfig ──
          if (itemKey === 'product1') {
            const shade = pdpState?.currentShade || Object.keys(bundleItems.product1ByVariant || {})[0] || 'default';
            const product1ByVariant = bundleItems.product1ByVariant || {};
            productData = product1ByVariant[shade] || Object.values(product1ByVariant)[0];
            desc = productData?.subtitle || upsellConfig.product1Desc || '';
            tagText = upsellConfig.product1TagText || '';
            showTag = upsellConfig.product1ShowTag !== false && tagText;
            socialText = upsellConfig.product1Social || '';
          } else if (itemKey === 'product2') {
            productData = bundleItems.product2;
            desc = productData?.subtitle || upsellConfig.product2Desc || '';
            socialText = upsellConfig.product2Social || '';
          }
        }

        if (!productData) return;

        const card = document.createElement('div');
        card.className = 'broglo-upsell__product';
        card.id = `brogloUpsell_${itemKey}`;
        card.dataset.upsellItem = itemKey;
        card.dataset.variantId = productData.variantId;
        card.dataset.price = productData.price;
        card.setAttribute('role', 'checkbox');
        card.setAttribute('aria-checked', 'false');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-label', `Add ${productData.title} to bundle`);
        card.onclick = () => BrogloUpsell.toggleItem(card);
        card.onkeydown = (e) => {
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); BrogloUpsell.toggleItem(card); }
        };

        const imgSrc = productData.image || '';
        const showSocialProof = upsellConfig.showSocialProof !== false && socialText;

        card.innerHTML = `
          <div class="broglo-upsell__product-check">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
          </div>
          <div class="broglo-upsell__product-img">
            ${imgSrc ? `<img src="${imgSrc}" alt="${productData.title}" loading="lazy">` : ''}
          </div>
          <div class="broglo-upsell__product-details">
            <div class="broglo-upsell__product-name" data-field="name">${productData.title}</div>
            <div class="broglo-upsell__product-desc">${desc}</div>
            ${showTag ? `<div class="broglo-upsell__product-tag">${tagText}</div>` : ''}
            ${showSocialProof ? `<div class="broglo-upsell__product-social">${socialText}</div>` : ''}
          </div>
          <div class="broglo-upsell__product-pricing">
            <div class="broglo-upsell__product-orig" data-field="orig" style="display:none"></div>
            <div class="broglo-upsell__product-sale" data-field="sale">${this.formatMoney(productData.price)}</div>
            <div class="broglo-upsell__product-save" data-field="save" style="display:none"></div>
          </div>
        `;

        container.appendChild(card);
      });

      this.updateCardPrices();
    },

    updateCardPrices() {
      const settings = window.brogloUpsellSettings || {};
      const alreadyCount = this.alreadyAddedItems.length;
      const selectedCount = Object.keys(this.selectedUpsells).length;
      const totalCount = alreadyCount + selectedCount;

      let discPct = 0;
      if (totalCount >= 3) discPct = settings.discount3 || 15;
      else if (totalCount >= 2) discPct = settings.discount2 || 10;

      // Update each card dynamically
      document.querySelectorAll('#brogloUpsellProducts .broglo-upsell__product').forEach(card => {
        const itemKey = card.dataset.upsellItem;
        const price = parseInt(card.dataset.price);
        const isSelected = this.selectedUpsells[itemKey];
        const effectiveDisc = isSelected ? discPct : 0;
        const salePrice = Math.round(price * (1 - effectiveDisc / 100));

        const origEl = card.querySelector('[data-field="orig"]');
        const saleEl = card.querySelector('[data-field="sale"]');
        const saveEl = card.querySelector('[data-field="save"]');

        if (effectiveDisc > 0) {
          if (origEl) { origEl.textContent = this.formatMoney(price); origEl.style.display = ''; }
          if (saleEl) saleEl.textContent = this.formatMoney(salePrice);
          if (saveEl) { saveEl.textContent = `Save ${effectiveDisc}%`; saveEl.style.display = ''; }
        } else {
          if (origEl) origEl.style.display = 'none';
          if (saleEl) saleEl.textContent = this.formatMoney(price);
          if (saveEl) saveEl.style.display = 'none';
        }
      });
    },

    /* ======================== CONFIRMATION ======================== */
    updateConfirmation(pdpState, pdpData, alreadyAdded) {
      const nameEl = document.getElementById('brogloUpsellAddedName');
      const priceEl = document.getElementById('brogloUpsellAddedPrice');
      if (!pdpData) return;

      const isTiered = this._pdpType === 'tiered';
      const variant = pdpData.variants.find(v => v.id === (pdpState?.currentVariantId || pdpData.currentVariantId)) || pdpData.variants[0];
      let nameStr = `${pdpData.productTitle} — ${variant?.title || 'Original'}`;

      if (isTiered) {
        // Tiered PDP: just show what was added
        const tierData = pdpData.tiers?.[pdpState?.currentTier || pdpData.defaultTier];
        if (tierData && tierData.qty > 1) {
          nameStr = `${tierData.qty}× ${pdpData.productTitle} — ${variant?.title || 'Original'}`;
        }
      } else {
        // Standard PDP: show bundle selections
        const sel = pdpState?.bundleSelections || {};
        if (sel.product1) {
          const shade = pdpState?.currentShade || 'original';
          const product1ByVariant = pdpData.bundleItems?.product1ByVariant || {};
          const upsell1Data = product1ByVariant[shade] || product1ByVariant[Object.keys(product1ByVariant)[0]] || Object.values(product1ByVariant)[0];
          nameStr += ` + ${upsell1Data?.title || 'Product 1'}`;
        }
        if (sel.product2) {
          nameStr += ` + ${pdpData.bundleItems?.product2?.title || 'Product 2'}`;
        }
      }

      nameStr += ' added to cart';
      if (nameEl) nameEl.textContent = nameStr;

      // Store full price for later reactivity
      let totalAdded = 0;
      alreadyAdded.forEach(item => {
        totalAdded += (item.quantity || 1) * (pdpData.variants.find(v => v.id === item.id)?.price || 0);
      });
      // If we didn't find price from variants, try to sum up from the items data
      if (totalAdded === 0) {
        alreadyAdded.forEach(item => {
          const faceMatch = pdpData.variants.find(v => v.id === item.id);
          if (faceMatch) { totalAdded += faceMatch.price * (item.quantity || 1); return; }
          if (!isTiered) {
            const product1ByVariant = pdpData.bundleItems?.product1ByVariant || {};
            for (const shade of Object.values(product1ByVariant)) {
              if (shade.variantId === item.id) { totalAdded += shade.price; return; }
            }
            if (pdpData.bundleItems?.product2 && item.id === pdpData.bundleItems.product2.variantId) {
              totalAdded += pdpData.bundleItems.product2.price;
            }
          }
        });
      }
      this._alreadyAddedFullPrice = totalAdded;
      if (priceEl) priceEl.textContent = this.formatMoney(totalAdded);
    },

    /* ======================== UPDATE CONFIRMATION PRICE ======================== */
    updateConfirmationPrice() {
      const priceEl = document.getElementById('brogloUpsellAddedPrice');
      if (!priceEl || !this._alreadyAddedFullPrice) return;

      const settings = window.brogloUpsellSettings || {};
      const selectedCount = Object.keys(this.selectedUpsells).length;
      const totalCount = this.alreadyAddedItems.length + selectedCount;

      let discPct = 0;
      if (totalCount >= 3) discPct = settings.discount3 || 15;
      else if (totalCount >= 2) discPct = settings.discount2 || 10;

      const fullPrice = this._alreadyAddedFullPrice;
      if (discPct > 0 && selectedCount > 0) {
        const discPrice = Math.round(fullPrice * (1 - discPct / 100));
        priceEl.innerHTML = `<span class="broglo-upsell__added-price-orig">${this.formatMoney(fullPrice)}</span> <span class="broglo-upsell__added-price-disc">${this.formatMoney(discPrice)} with bundle</span>`;
      } else if (discPct > 0 && selectedCount === 0) {
        // Nothing selected yet — show what they COULD save
        const possibleCount = this.alreadyAddedItems.length + this.missingItems.length;
        let possibleDisc = 0;
        if (possibleCount >= 3) possibleDisc = settings.discount3 || 15;
        else if (possibleCount >= 2) possibleDisc = settings.discount2 || 10;
        if (possibleDisc > 0) {
          const possiblePrice = Math.round(fullPrice * (1 - possibleDisc / 100));
          priceEl.innerHTML = `${this.formatMoney(fullPrice)} — <span class="broglo-upsell__added-price-disc">could be ${this.formatMoney(possiblePrice)}</span>`;
        } else {
          priceEl.textContent = this.formatMoney(fullPrice);
        }
      } else {
        priceEl.textContent = this.formatMoney(fullPrice);
      }
    },

    /* ======================== TIER PROGRESS BAR ======================== */
    updateTierBar() {
      const fill = document.getElementById('brogloUpsellTierFill');
      const track = document.getElementById('brogloUpsellTierTrack');
      if (!fill) return;

      const settings = window.brogloUpsellSettings || {};
      const disc2 = settings.discount2 || 10;
      const disc3 = settings.discount3 || 15;
      const selectedCount = Object.keys(this.selectedUpsells).length;
      const totalCount = this.alreadyAddedItems.length + selectedCount;

      // Update fill bar
      fill.dataset.items = Math.min(totalCount, 3).toString();

      // Update ARIA progressbar
      if (track) track.setAttribute('aria-valuenow', Math.min(totalCount, 3).toString());

      // Dot 1: always reached (trigger product already added)
      const dot1 = document.getElementById('brogloTierDot1');
      const label1 = document.getElementById('brogloTierLabel1');
      if (dot1) dot1.classList.add('reached');
      if (label1) { label1.classList.add('reached'); label1.textContent = ''; }

      // Dot 2: reached if 2+ items
      const dot2 = document.getElementById('brogloTierDot2');
      const label2 = document.getElementById('brogloTierLabel2');
      const nudge2 = document.getElementById('brogloTierNudge2');
      if (dot2) dot2.classList.toggle('reached', totalCount >= 2);
      if (label2) {
        label2.classList.toggle('reached', totalCount >= 2);
        label2.textContent = totalCount >= 2 ? `${disc2}% off ✓` : `${disc2}% off`;
      }
      // Nudge: show what to add to reach this tier
      if (nudge2) {
        if (totalCount < 2 && this.missingItems.length > 0) {
          nudge2.textContent = '+ add 1 item';
        } else {
          nudge2.textContent = '';
        }
      }

      // Dot 3: reached if 3+ items
      const dot3 = document.getElementById('brogloTierDot3');
      const label3 = document.getElementById('brogloTierLabel3');
      const nudge3 = document.getElementById('brogloTierNudge3');
      if (dot3) dot3.classList.toggle('reached', totalCount >= 3);
      if (label3) {
        label3.classList.toggle('reached', totalCount >= 3);
        label3.textContent = totalCount >= 3 ? `${disc3}% off ✓` : `${disc3}% off`;
      }
      // Nudge: show what product unlocks this tier
      if (nudge3) {
        if (totalCount === 2 && this.missingItems.length > 0) {
          // Find the unchecked missing item name
          const unchecked = this.missingItems.filter(i => !this.selectedUpsells[i]);
          if (unchecked.length > 0) {
            const card = document.getElementById(`brogloUpsell_${unchecked[0]}`);
            const productName = card?.querySelector('[data-field="name"]')?.textContent || unchecked[0];
            nudge3.textContent = `+ add ${productName.toLowerCase()}`;
          } else {
            nudge3.textContent = '';
          }
        } else if (totalCount < 2 && this.missingItems.length >= 2) {
          nudge3.textContent = '+ add both';
        } else {
          nudge3.textContent = '';
        }
      }
    },

    /* ======================== PITCH ======================== */
    updatePitch(missingItems, pdpData) {
      const titleEl = document.getElementById('brogloUpsellPitchTitle');
      const textEl = document.getElementById('brogloUpsellPitchText');
      const settings = window.brogloUpsellSettings || {};
      const upsellConfig = pdpData?.upsellConfig || {};
      const disc2 = settings.discount2 || 10;
      const disc3 = settings.discount3 || 15;

      if (missingItems.length === 2) {
        // Both missing — use PDP config or Liquid defaults
        if (titleEl && upsellConfig.pitchTitle) titleEl.textContent = upsellConfig.pitchTitle;
        if (textEl && upsellConfig.pitchSubtext) {
          const highlight = upsellConfig.pitchHighlight || `Add both to save ${disc3}%`;
          textEl.innerHTML = `${upsellConfig.pitchSubtext} — <span class="broglo-upsell__highlight">${highlight}</span>`;
        }
      } else if (missingItems.length === 1) {
        // Calculate what tier adding this item would unlock
        const wouldBe = this.alreadyAddedItems.length + 1;
        const tierDisc = wouldBe >= 3 ? disc3 : (wouldBe >= 2 ? disc2 : 0);
        const itemKey = missingItems[0];

        // Get product name from rendered card
        const card = document.getElementById(`brogloUpsell_${itemKey}`);
        const productName = card?.querySelector('[data-field="name"]')?.textContent || itemKey;

        if (titleEl) titleEl.textContent = 'Add the finishing touch';
        if (textEl) textEl.innerHTML = `Add the ${productName.toLowerCase()} — <span class="broglo-upsell__highlight">save ${tierDisc}% on your entire order</span>`;
      }
    },

    /* ======================== TOGGLE ======================== */
    toggleItem(el) {
      const item = el.dataset.upsellItem;
      if (el.classList.contains('selected')) {
        el.classList.remove('selected');
        el.setAttribute('aria-checked', 'false');
        delete this.selectedUpsells[item];
      } else {
        el.classList.add('selected');
        el.setAttribute('aria-checked', 'true');
        this.selectedUpsells[item] = {
          variantId: parseInt(el.dataset.variantId),
          price: parseInt(el.dataset.price)
        };
      }
      this.updateCardPrices();
      this.updateConfirmationPrice();
      this.updateTierBar();
      this.updateCta();
      this.announceToScreenReader();
    },

    /* ======================== UPDATE CTA ======================== */
    updateCta() {
      const ctaBtn = document.getElementById('brogloUpsellCtaBtn');
      const skipBtn = document.getElementById('brogloUpsellSkip');
      const savingsBar = document.getElementById('brogloUpsellSavingsBar');
      const comboBadge = document.getElementById('brogloUpsellComboBadge');

      const selectedCount = Object.keys(this.selectedUpsells).length;
      const visibleCards = this.missingItems.length;

      if (selectedCount === 0) {
        if (ctaBtn) {
          ctaBtn.textContent = 'Continue to Cart';
          ctaBtn.setAttribute('data-style', 'secondary');
        }
        if (skipBtn) skipBtn.textContent = 'No thanks, just what I have →';
        if (savingsBar) savingsBar.classList.remove('visible');
        if (comboBadge) comboBadge.classList.remove('visible');
      } else {
        // Calculate total across ALL items (trigger product + upsells) since discount applies to everything
        const settings = window.brogloUpsellSettings || {};
        const pdpData = window.brogloPdpData?.[this.sourceSectionId];
        const newTotalCount = this.alreadyAddedItems.length + selectedCount;
        let discPct = 0;
        if (newTotalCount >= 3) discPct = settings.discount3 || 15;
        else if (newTotalCount >= 2) discPct = settings.discount2 || 10;

        // Sum up ALL items: already added + upsell selections
        let allFullTotal = 0;
        this.alreadyAddedItems.forEach(item => {
          // Look up price from PDP data
          if (pdpData) {
            const faceMatch = pdpData.variants.find(v => v.id === item.id);
            if (faceMatch) { allFullTotal += faceMatch.price; return; }
            const product1ByVariant = pdpData.bundleItems?.product1ByVariant || {};
            for (const shade of Object.values(product1ByVariant)) {
              if (shade.variantId === item.id) { allFullTotal += shade.price; return; }
            }
            if (pdpData.bundleItems?.product2 && item.id === pdpData.bundleItems.product2.variantId) {
              allFullTotal += pdpData.bundleItems.product2.price;
            }
          }
        });

        let upsellFullTotal = 0;
        Object.values(this.selectedUpsells).forEach(item => {
          upsellFullTotal += item.price;
        });

        allFullTotal += upsellFullTotal;
        const allDiscounted = Math.round(allFullTotal * (1 - discPct / 100));
        const totalSavings = allFullTotal - allDiscounted;

        // CTA shows only the upsell items' discounted price (what they're adding now)
        const upsellDiscounted = Math.round(upsellFullTotal * (1 - discPct / 100));

        if (ctaBtn) {
          const label = selectedCount === 1 ? 'Add 1 to Cart' : `Add ${selectedCount} to Cart`;
          if (discPct > 0) {
            ctaBtn.innerHTML = `${label} — <span style="text-decoration:line-through;opacity:0.6;font-weight:500">${this.formatMoney(upsellFullTotal)}</span> ${this.formatMoney(upsellDiscounted)}`;
          } else {
            ctaBtn.textContent = `${label} — ${this.formatMoney(upsellFullTotal)}`;
          }
          ctaBtn.removeAttribute('data-style');
        }

        if (skipBtn) skipBtn.textContent = 'Remove add-ons →';

        // Savings bar shows TOTAL savings across all items with trigger product callout
        if (savingsBar && totalSavings > 0) {
          // Calculate just the trigger product savings
          let faceTannerFull = 0;
          this.alreadyAddedItems.forEach(item => {
            if (pdpData) {
              const faceMatch = pdpData.variants.find(v => v.id === item.id);
              if (faceMatch) faceTannerFull += faceMatch.price;
            }
          });
          const faceTannerSavings = Math.round(faceTannerFull * discPct / 100);

          if (faceTannerSavings > 0) {
            // Get product name from PDP data
            const triggerProductName = pdpData?.productTitle || 'your product';
            savingsBar.innerHTML = `🎉 You're saving ${this.formatMoney(totalSavings)} total — <strong>includes ${this.formatMoney(faceTannerSavings)} off ${triggerProductName}!</strong>`;
          } else {
            savingsBar.textContent = `🎉 You're saving ${this.formatMoney(totalSavings)} with bundle pricing!`;
          }
          savingsBar.classList.add('visible');
        } else if (savingsBar) {
          savingsBar.classList.remove('visible');
        }

        // Combo badge: show when ALL visible items selected
        if (comboBadge) {
          if (selectedCount >= visibleCards && visibleCards >= 2) comboBadge.classList.add('visible');
          else comboBadge.classList.remove('visible');
        }
      }
    },

    /* ======================== ADD SELECTED ======================== */
    async addSelected() {
      const selectedCount = Object.keys(this.selectedUpsells).length;

      if (selectedCount === 0) {
        this.close();
        return;
      }

      const ctaBtn = document.getElementById('brogloUpsellCtaBtn');

      // ── Step 1: Instant visual feedback before any network call ──
      if (ctaBtn) {
        ctaBtn.setAttribute('aria-busy', 'true');
        ctaBtn.setAttribute('aria-disabled', 'true');
        ctaBtn._prevText = ctaBtn.innerHTML;
        ctaBtn.innerHTML = '<span class="broglo-upsell__cta-spinner"></span> Adding...';
      }

      const { pdpData } = this._resolvePdp(this.sourceSectionId);
      const prop = pdpData?.discountProperty;
      const settings = window.brogloUpsellSettings || {};
      const totalCount = this.alreadyAddedItems.length + selectedCount;
      const bundleTag = 'true';

      const items = Object.entries(this.selectedUpsells).map(([key, data]) => {
        const item = { id: data.variantId, quantity: 1 };
        if (prop) item.properties = { ['_' + prop]: bundleTag };
        return item;
      });

      try {
        // ── Step 2: Primary add — only thing the customer waits for ──
        const response = await fetch(
          (window.Shopify?.routes?.root || '/') + 'cart/add.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items })
          }
        );

        if (!response.ok) throw new Error('Failed to add items');

        // ── Step 3: Success UI + close immediately — customer is unblocked ──
        if (ctaBtn) {
          ctaBtn.innerHTML = '✓ Added!';
          ctaBtn.style.setProperty('background', '#2D9B5E', 'important');
        }

        sessionStorage.removeItem('broglo_upsell_timer');

        // Close and open cart without waiting for background tagging
        setTimeout(() => { this.close(); }, 400);

        // ── Step 4: Background tagging — fire and forget, customer never waits ──
        if (prop && this.alreadyAddedItems.length > 0) {
          this._backgroundTagBundleItems(prop, bundleTag, totalCount);
        } else if (prop) {
          // Still update cart attributes even with no existing items to tag
          fetch(
            (window.Shopify?.routes?.root || '/') + 'cart/update.js',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ attributes: { '_broglo_bundle_count': totalCount.toString() } })
            }
          ).catch(e => console.warn('[Bro Glo Upsell] Background cart update failed:', e));
        }

      } catch (err) {
        console.error('Upsell fetch error:', err);
        if (ctaBtn && ctaBtn._prevText) ctaBtn.innerHTML = ctaBtn._prevText;
        if (ctaBtn) { ctaBtn.removeAttribute('aria-disabled'); ctaBtn.removeAttribute('aria-busy'); }
      }
    },

    /* ======================== BACKGROUND BUNDLE TAGGING ======================== */
    /** Non-blocking: tags trigger product + sets cart attributes after upsell items already added */
    async _backgroundTagBundleItems(prop, bundleTag, totalCount) {
      try {
        // Fetch cart and update attributes in parallel
        const [cartResp] = await Promise.all([
          fetch((window.Shopify?.routes?.root || '/') + 'cart.js',
            { headers: { 'X-Requested-With': 'XMLHttpRequest' } }),
          fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ attributes: { '_broglo_bundle_count': totalCount.toString() } })
          }).catch(e => console.warn('[Bro Glo Upsell] Cart attribute update failed:', e))
        ]);

        const cart = await cartResp.json();

        // Tag each already-added item — fire all updates in parallel
        const updatePromises = this.alreadyAddedItems.map(addedItem => {
          const cartLine = cart.items.find(ci => ci.variant_id === addedItem.id);
          if (!cartLine) return Promise.resolve();
          const updatedProps = { ...(cartLine.properties || {}), ['_' + prop]: bundleTag };
          return fetch((window.Shopify?.routes?.root || '/') + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              line: cart.items.indexOf(cartLine) + 1,
              quantity: cartLine.quantity,
              properties: updatedProps
            })
          }).catch(e => console.warn('[Bro Glo Upsell] Line item tag failed:', e));
        });

        await Promise.all(updatePromises);

      } catch (err) {
        console.warn('[Bro Glo Upsell] Background tagging failed (non-fatal):', err);
      }
    },


    /* ======================== UTILITIES ======================== */
    formatMoney(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) return '$0.00';
      return '$' + (cents / 100).toFixed(2);
    },

    /* ======================== FOCUS TRAP ======================== */
    _setupFocusTrap() {
      const overlay = document.getElementById('brogloUpsellOverlay');
      if (!overlay) return;
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        const focusable = overlay.querySelectorAll(
          'button, [href], [tabindex]:not([tabindex="-1"]), input, select, textarea, [role="checkbox"]'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    },

    _removeFocusTrap() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    },

    /* ======================== SCREEN READER ======================== */
    announceToScreenReader() {
      const liveEl = document.getElementById('brogloUpsellLive');
      if (!liveEl) return;
      const settings = window.brogloUpsellSettings || {};
      const selectedCount = Object.keys(this.selectedUpsells).length;
      const totalCount = this.alreadyAddedItems.length + selectedCount;
      let discPct = 0;
      if (totalCount >= 3) discPct = settings.discount3 || 15;
      else if (totalCount >= 2) discPct = settings.discount2 || 10;

      const items = Object.keys(this.selectedUpsells).map(k => {
        const card = document.getElementById(`brogloUpsell_${k}`);
        return card?.querySelector('[data-field="name"]')?.textContent || k;
      });
      let msg = selectedCount > 0
        ? `${selectedCount} add-on${selectedCount > 1 ? 's' : ''} selected: ${items.join(' and ')}.`
        : 'No add-ons selected.';
      if (discPct > 0) msg += ` ${discPct}% bundle discount applied.`;
      liveEl.textContent = msg;
    }
  };

  window.BrogloUpsell = BrogloUpsell;

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && BrogloUpsell.isOpen) BrogloUpsell.close();
  });

})();
